import { Organization } from "@prisma/client";
import {
  createContext,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ROUTE_DASHBOARD_ROOT,
  ROUTE_PUBLIC_ROOT,
} from "../../shared/app-constants";
import { AppState } from "../../shared/app-state";
import getDashboardRoute from "../../shared/get-dashboard-route";
import getPublicRoute from "../../shared/get-public-route";
import { LocalStorageManager } from "../../shared/local-storage-manager";
import UserModel from "../../shared/user-model";
import HttpClient, { refreshToken } from "../http-client";

interface AppContextType {
  user?: UserModel;
  organization?: Organization;
  setUser: (user?: UserModel) => void;
  isAuthenticated?: boolean;
  appState?: AppState;
  appRoot?: string;
}

export const AppContext = createContext<AppContextType>({} as AppContextType);

const AppContextProvider: FunctionComponent<{}> = ({ children }) => {
  const [user, setUser] = useState<UserModel>();
  const [appRoot, setAppRoot] = useState<string>();
  const [appState, setAppState] = useState<AppState>(AppState.Loading);

  const isAuthenticated = useMemo(() => !!user, [user]);
  const organization = useMemo(() => (user as any)?.organization, [user]);
  useEffect(() => {
    if (user) {
      setAppRoot(getDashboardRoute({}, ROUTE_DASHBOARD_ROOT));
    } else {
      setAppRoot(getPublicRoute({}, ROUTE_PUBLIC_ROOT));
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      let { refresh_token } = LocalStorageManager.getTokens();
      if (refresh_token) {
        const renewResult = await refreshToken();
        if (renewResult) {
          try {
            const { data } = await HttpClient.get("user/profile");
            if (data?.data?.id) {
              setUser(data.data);
            } else {
              throw new Error("User not found!");
            }
          } catch (e) {
            console.log(e);
          }
        }
      }
      setAppState(AppState.Loaded);
    })();
  }, []);

  return (
    <AppContext.Provider
      value={{
        user,
        isAuthenticated,
        appState,
        setUser,
        appRoot,
        organization,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
